import * as React from 'react';
import { useState } from 'react';

import { useMutation } from '@apollo/client';
import * as Sentry from '@sentry/react';
import { LearnerOnboarding_ModularOnboardingInteractionType as ModularOnboardingInteraction } from '__generated__/graphql-types';

import InformationBanner from 'bundles/onboarding-common/components/InformationBanner';
import ViewOnboardingModuleVisibilityTracker from 'bundles/onboarding-common/components/ViewOnboardingModuleVisibilityTracker';
import { PATHWAYS_ONBOARDING_MODULAR_EVENT_NAME } from 'bundles/onboarding-common/constants';
import useSetOnboardingModuleInteraction from 'bundles/onboarding-common/hooks/useSetOnboardingModuleInteraction';
import useTrackOnboardingModuleButtonClick from 'bundles/onboarding-common/hooks/useTrackOnboardingModuleButtonClicks';
import type { RoleFragment } from 'bundles/onboarding-common/queries/__generated__/RoleFragment';
import UpdateCareerRoleMutation from 'bundles/unified-home-common/queries/UpdateCareerRoleMutation.graphql';
import type {
  UpdateCareerRoleMutation as UpdateCareerRoleMutationType,
  UpdateCareerRoleMutationVariables,
} from 'bundles/unified-home-common/queries/__generated__/UpdateCareerRoleMutation';

import _t from 'i18n!nls/onboarding-common';

type Props = {
  inferredRole: Pick<RoleFragment, 'name' | 'id'>;
  onConfirmRoleSuccess: () => void;
  onDismiss: () => void;
};

const InferredRoleModuleRenderer = ({ inferredRole, onConfirmRoleSuccess, onDismiss }: Props) => {
  const { trackClickOnboardingModuleButton } = useTrackOnboardingModuleButtonClick();
  const [isInferredRoleBannerDismissed, setIsInferredRoleBannerDismissed] = useState(false);
  const [isSettingCareerRole, setIsSettingCareerRole] = useState(false);
  const [updateCareerRoleMutation] = useMutation<UpdateCareerRoleMutationType, UpdateCareerRoleMutationVariables>(
    UpdateCareerRoleMutation,
    {
      context: { clientName: 'gatewayGql' },
      variables: {
        input: {
          occupationPreferences: {
            occupationPreferences: [
              {
                occupation: {
                  id: inferredRole.id,
                },
              },
            ],
          },
        },
      },
    }
  );
  const { setOnboardingModuleInteraction } = useSetOnboardingModuleInteraction();

  const handleInferredRoleBannerDismissal = () => {
    setIsInferredRoleBannerDismissed(true);
    trackClickOnboardingModuleButton({
      buttonName: 'onboarding_module_dismiss',
      moduleType: 'inferred_role',
    });
    onDismiss();
  };

  const handleConfirmRole = async () => {
    setIsSettingCareerRole(true);
    try {
      const response = await updateCareerRoleMutation();
      const result = response.data?.LearnerProfile_PatchWorkPreferences;
      const error = result?.__typename === 'LearnerProfile_PatchFailure';

      if (error) {
        // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
        throw new Error(result.errors[0].message ?? 'Failed to update career role');
      }
      await setOnboardingModuleInteraction(ModularOnboardingInteraction.RoleConfirmation);

      trackClickOnboardingModuleButton({
        buttonName: 'confirm_inferred_role',
        formEventData: {
          name: PATHWAYS_ONBOARDING_MODULAR_EVENT_NAME,
          value: [inferredRole.id],
        },
        moduleType: 'inferred_role',
      });

      onConfirmRoleSuccess();
    } catch (error) {
      setIsSettingCareerRole(false);
      Sentry.captureException(error, {
        extra: {
          message: error.message,
        },
      });
    }
  };

  if (!inferredRole?.id) {
    return null;
  }

  return (
    <ViewOnboardingModuleVisibilityTracker moduleType="inferred_role">
      <InformationBanner
        headerText={_t('Exploring the #{role} role?', { role: inferredRole.name })}
        bodyText={_t('Set it as your role and get personalized recommendations')}
        isDismissed={isInferredRoleBannerDismissed}
        onDismiss={handleInferredRoleBannerDismissal}
        ctaButton={{
          label: _t('Confirm role'),
          onClick: handleConfirmRole,
          isLoading: isSettingCareerRole,
        }}
      />
    </ViewOnboardingModuleVisibilityTracker>
  );
};

export default InferredRoleModuleRenderer;
